/**
 * Utility functions for handling paid_data in localStorage
 */

import { MyBatches } from '@/hooks/useMyBatches';
import { BatchType } from '@/types/batch';
import { setLocalStorageValue } from './common';

export interface PaidData {
  user_status?: 'non_loggedin' | 'free' | 'paid';
  paid_batch_type?: 'booster' | 'master' | 'both';
  [key: string]: any;
}

/**
 * Get paid data from localStorage
 */
export const getPaidData = (): PaidData => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const paidDataString = localStorage.getItem('paidData');
    return paidDataString ? JSON.parse(paidDataString) : {};
  } catch (error) {
    console.error('Error parsing paid data from localStorage:', error);
    return {};
  }
};

/**
 * Set paid data in localStorage
 */
export const setPaidData = (paidData: PaidData): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem('paidData', JSON.stringify(paidData));
  } catch (error) {
    console.error('Error setting paid data in localStorage:', error);
  }
};

/**
 * Clear paid data from localStorage
 */
export const clearPaidData = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.removeItem('paidData');
  } catch (error) {
    console.error('Error clearing paid data from localStorage:', error);
  }
};

/**
 * Update specific fields in paid data
 */
export const updatePaidData = (updates: Partial<PaidData>): void => {
  const currentPaidData = getPaidData();
  const updatedPaidData = { ...currentPaidData, ...updates };
  setPaidData(updatedPaidData);
};

/**
 * Get user status from paid data
 */
export const getUserStatus = (): string => {
  const paidData = getPaidData();
  return paidData.user_status || '';
};

/**
 * Get paid batch type from paid data
 */
export const getPaidBatchType = (): string => {
  const paidData = getPaidData();
  return paidData.paid_batch_type || '';
};

/**
 * Set user status in paid data
 */
export const setUserStatus = (userStatus: 'non_loggedin' | 'free' | 'paid'): void => {
  updatePaidData({ user_status: userStatus });
};

/**
 * Set paid batch type in paid data
 */
export const setPaidBatchType = (paidBatchType: 'booster' | 'master' | 'both'): void => {
  updatePaidData({ paid_batch_type: paidBatchType });
};

export const setLocalPaidData = (purchasedBatches: MyBatches[] | null) => {
  if (purchasedBatches === null) {
    const paidData = {
      user_status: 'non_loggedin',
    };

    window.localStorage.setItem('paidData', JSON.stringify(paidData));
  }
  const filteredPurchasedBatches: MyBatches[] = (purchasedBatches || [])?.filter(
    (batchData: MyBatches) => batchData?.isPurchased && batchData.type !== 'WEBINAR'
  );
  const isPaidUser = filteredPurchasedBatches && filteredPurchasedBatches.length !== 0;

  let isBoosterBatchPurchased = false;
  let isMasterBatchPurchased = false;
  filteredPurchasedBatches.forEach(batchData => {
    const batchType = batchData?.config?.cjrBatchType;

    if (batchType === BatchType.BOOSTER) {
      isBoosterBatchPurchased = true;
    } else {
      isMasterBatchPurchased = true;
    }
  });

  let paidBatchType: 'booster' | 'master' | 'both' | undefined;
  if (isBoosterBatchPurchased && isMasterBatchPurchased) {
    paidBatchType = 'both';
  } else if (isBoosterBatchPurchased) {
    paidBatchType = 'booster';
  } else if (isMasterBatchPurchased) {
    paidBatchType = 'master';
  }

  let userStatus;
  if (isPaidUser) {
    userStatus = 'paid';
  } else {
    userStatus = 'free';
  }

  const paidData = {
    user_status: userStatus,
    paid_batch_type: paidBatchType,
  };

  setLocalStorageValue('paidData', JSON.stringify(paidData));
};
